import React from "react";
import {FaEnvelope, FaExclamationTriangle, FaFlag, FaShieldAlt} from "react-icons/fa";
import ContentPage from "../../components/ContentPage";

const ChildSafety = () => (
	<ContentPage
		icon={<FaShieldAlt/>}
		eyebrow="SAFETY COMES FIRST"
		title="Child Safety Standards"
		summary="Barkr is an adults-only community with zero tolerance for child sexual abuse, exploitation, or endangerment."
		updated="Effective 3 April 2026"
		asideTitle="Report a concern"
		asideText="If you encounter content or behavior that may put a child at risk, report it immediately."
		links={[
			{label: "Email the safety team", href: "mailto:safety@barkr.app"},
			{label: "Contact support", href: "mailto:support@barkr.app"},
			{label: "Privacy Policy", href: "/privacy"},
			{label: "Terms of Service", href: "/terms"},
		]}>
		<div className="content-page__notice">
			<strong><FaExclamationTriangle/> Immediate danger</strong><br/>
			If a child may be in immediate danger, contact your local emergency services before
			reporting the concern to Barkr.
		</div>

		<h2>1. Adults only</h2>
		<p>
			Barkr is designed exclusively for adults aged 18 and older. We do not knowingly allow
			anyone under 18 to create an account or use the platform. Every member must confirm that
			they meet this age requirement during registration.
		</p>

		<h2>2. Zero-tolerance policy</h2>
		<p>
			Barkr prohibits any content or behavior involving child sexual abuse or exploitation.
			Prohibited activity includes:
		</p>
		<ul>
			<li>Imagery or content that depicts or sexualizes minors.</li>
			<li>Solicitation, grooming, or sexual communication involving minors.</li>
			<li>Sharing, distributing, requesting, or possessing child sexual abuse material.</li>
			<li>Attempts to meet a minor for a sexual purpose.</li>
			<li>Any other activity that exploits, endangers, or facilitates harm to a child.</li>
		</ul>

		<h2>3. How Barkr detects and responds</h2>
		<p>We use multiple safeguards to identify and address potential violations:</p>
		<ul>
			<li><strong>Automated systems:</strong> tools that help detect potentially harmful content and activity.</li>
			<li><strong>Human review:</strong> trained team members review reports and flagged content.</li>
			<li><strong>Member reporting:</strong> reporting controls are available on profiles and messages inside the app.</li>
		</ul>

		<h2>4. How to report</h2>
		<p>
			Report suspicious content or behavior as soon as possible. Reports are handled promptly
			and as confidentially as circumstances allow.
		</p>
		<div className="content-page__contact-grid">
			<a className="content-page__contact-card" href="mailto:safety@barkr.app">
				<span><FaFlag/></span>
				<div>
					<strong>Safety report</strong>
					<small>safety@barkr.app<br/>For suspected exploitation, abuse, or child endangerment.</small>
				</div>
			</a>
			<a className="content-page__contact-card" href="mailto:support@barkr.app">
				<span><FaEnvelope/></span>
				<div>
					<strong>General support</strong>
					<small>support@barkr.app<br/>For account help and other safety questions.</small>
				</div>
			</a>
		</div>
		<p>
			You can also use the <strong>Report</strong> action on a Barkr profile or message. Include
			enough context for our team to locate the relevant account or content, but do not forward
			or reproduce illegal material.
		</p>

		<h2>5. Cooperation with authorities</h2>
		<p>
			Barkr cooperates with law enforcement agencies investigating child sexual abuse and
			exploitation. Where required or appropriate, we may:
		</p>
		<ul>
			<li>Report suspected abuse or exploitation to NCMEC and relevant authorities.</li>
			<li>Preserve evidence in accordance with applicable law.</li>
			<li>Respond to valid legal requests for information.</li>
		</ul>

		<h2>6. Enforcement</h2>
		<p>Accounts that violate these standards may face immediate action, including:</p>
		<ul>
			<li>Content removal and immediate account suspension or permanent removal.</li>
			<li>Reporting to law enforcement or other appropriate authorities.</li>
			<li>Preservation of relevant account information and evidence for legal proceedings.</li>
		</ul>

		<h2>7. Team training</h2>
		<p>
			Barkr team members who may encounter member content or handle reports receive guidance
			on identifying potential abuse, following escalation procedures, and handling sensitive
			information appropriately.
		</p>

		<h2>8. Continuous improvement</h2>
		<p>
			We regularly review our child-safety safeguards, moderation procedures, and reporting
			tools to respond to evolving risks and recognized industry practices.
		</p>

		<div className="content-page__notice">
			<strong>Questions about these standards?</strong><br/>
			Contact the Barkr safety team at{" "}
			<a href="mailto:safety@barkr.app">safety@barkr.app</a>.
		</div>
	</ContentPage>
);

export default ChildSafety;
